import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Radio, FormControlLabel, RadioGroup, FormControl, useTheme, useMediaQuery } from '@mui/material';
import Image_Free_1 from '../assets/images/Image_Free_1.png';
import ReCAPTCHA from 'react-google-recaptcha';

const RECAPTCHA_KEY = process.env.NODE_ENV === 'development' ? null : process.env.GATSBY_RECAPTCHA_KEY;

const ContactSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [isThankyou, setIsThankyou] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    location: '',
    projectDescription: '',
    techStack: '',
    timeline: '',
    date: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
  };

  const handleSubmitHubspot = async (e: React.FormEvent) => {
    e.preventDefault();

    // If reCAPTCHA is enabled but not verified, don't submit
    if (RECAPTCHA_KEY && !recaptchaValue) {
      alert('Please verify that you are not a robot');
      return;
    }

    try {
      const url = 'https://api.hsforms.com/submissions/v3/integration/submit/5727045/d29faeaf-87ea-417f-9386-02d84b16f583';
      const data = {
        fields: [
          {
            name: 'email',
            value: formData.email,
          },
          {
            name: 'firstname',
            value: formData.firstName,
          },
          {
            name: 'lastname',
            value: formData.lastName,
          },
          {
            name: 'country',
            value: formData.location,
          },
          {
            name: '0-2/description',
            value: formData.projectDescription,
          },
          {
            name: '0-2/tech_stack',
            value: formData.techStack,
          },
          {
            name: '0-2/timeline',
            value: formData.timeline,
          },
          {
            name: 'set_up_call',
            value: formData.date,
          },
        ],
        context: {
          pageUri: 'www.alioit.com',
          pageName: 'Alio IT',
        },
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setIsThankyou(true);
        setFormData({
          email: '',
          firstName: '',
          lastName: '',
          location: '',
          projectDescription: '',
          techStack: '',
          timeline: '',
          date: '',
        });
        setRecaptchaValue(null);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
    }
  };

  return (
    <Box
      id="contact-section"
      sx={{
        width: '100%',
        minHeight: { xs: 'auto', md: '1000px' },
        position: 'relative',
        background: '#050505',
        py: { xs: 6, md: 0 },
        overflow: 'hidden',
      }}
    >
      <Box
        component="img"
        src={Image_Free_1}
        alt="Background"
        sx={{
          width: { xs: '100%', md: '596px' },
          maxWidth: '596px',
          height: 'auto',
          position: { xs: 'absolute', md: 'absolute' },
          right: { xs: '-50%', sm: '-30%', md: '10%' },
          top: { xs: '5%', md: '0.23px' },
          transform: { xs: 'translateX(30%)', md: 'none' },
          opacity: 0.35,
          zIndex: 0,
        }}
      />
      
      <Box
        component="form"
        onSubmit={handleSubmitHubspot}
        sx={{
          width: { xs: '90%', sm: '95%', md: '80%', lg: '910px' },
          maxWidth: '910px',
          padding: { xs: '20px', sm: '30px', md: '40px' },
          mx: { xs: 'auto', md: 'unset' },
          left: { xs: '0', md: '10%', lg: '150px' },
          right: { xs: '0', md: 'unset' },
          position: { xs: 'relative', md: 'absolute' },
          top: { xs: '0', md: '50px' },
          background: 'linear-gradient(133deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0) 100%)',
          borderRadius: '25px',
          outline: '1px #6DFCFF solid',
          outlineOffset: '-1px',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: '30px', md: '40px' },
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: '379px' },
            display: 'flex',
            flexDirection: 'column',
            gap: '25px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
          >
            <Box
              sx={{
                color: 'white',
                fontSize: { xs: '28px', md: '36px' },
                fontFamily: 'Raleway',
                fontWeight: 600,
              }}
            >
              Head Quarters
            </Box>
            <Box
              sx={{
                color: 'white',
                fontSize: { xs: '16px', md: '18px' },
                fontFamily: 'Raleway',
                lineHeight: '26px',
              }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>USA</Box>
              <Box sx={{ height: '10px' }} />
              <Box component="span" sx={{ fontWeight: 400 }}>
                Phone: 520 367 2544<br/>
                9040 S Rita Rd #1270<br/>
                Tucson, AZ 85747
              </Box>
              <Box sx={{ height: '20px' }} />
              <Box component="span" sx={{ fontWeight: 400 }}>
                7670 Opportunity Rd #255<br/>
                San Diego, CA 92111
              </Box>
              <Box sx={{ height: '45px' }} />
              <Box component="span" sx={{ fontWeight: 700 }}>Mexico</Box>
              <Box sx={{ height: '10px' }} />
              <Box component="span" sx={{ fontWeight: 400 }}>
                Garmendia #202-I, Col.Centro<br/>
                Hermosillo, Son. 83000
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            width: { xs: '100%', md: 'auto' },
          }}
        >
          {isThankyou ? (
            <Box>
              <Typography
                sx={{
                  color: 'white',
                  fontSize: { xs: '32px', md: '42px' },
                  fontFamily: 'Raleway',
                  fontWeight: 600,
                  marginBottom: '15px',
                }}
              >
                Thanks for contacting us.
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '16px',
                  fontFamily: 'Raleway',
                }}
              >
                Your message has been successfully sent. We will contact you very soon!
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  color: 'white',
                  fontSize: { xs: '28px', md: '36px' },
                  fontFamily: 'Raleway',
                  fontWeight: 600,
                  marginBottom: '8px',
                }}
              >
                Talk To Us
              </Box>

              <TextField
                name="email"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
                required
                sx={{
                  width: { xs: '100%', md: '303px' },
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    outline: '1px rgba(255, 255, 255, 0.50) solid',
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                    '&:hover fieldset': {
                      borderColor: 'transparent',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'transparent',
                    },
                    '& input': {
                      color: '#BBBBBB',
                      fontSize: '14px',
                      fontFamily: 'Raleway',
                    },
                  },
                }}
              />

              <TextField
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                required
                sx={{
                  width: { xs: '100%', md: '303px' },
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    outline: '1px rgba(255, 255, 255, 0.50) solid',
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                    '&:hover fieldset': {
                      borderColor: 'transparent',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'transparent',
                    },
                    '& input': {
                      color: '#BBBBBB',
                      fontSize: '14px',
                      fontFamily: 'Raleway',
                    },
                  },
                }}
              />

              <TextField
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                required
                sx={{
                  width: { xs: '100%', md: '303px' },
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    outline: '1px rgba(255, 255, 255, 0.50) solid',
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                    '&:hover fieldset': {
                      borderColor: 'transparent',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'transparent',
                    },
                    '& input': {
                      color: '#BBBBBB',
                      fontSize: '14px',
                      fontFamily: 'Raleway',
                    },
                  },
                }}
              />

              <TextField
                name="location"
                placeholder="Country/Region"
                value={formData.location}
                onChange={handleChange}
                required
                sx={{
                  width: { xs: '100%', md: '303px' },
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    outline: '1px rgba(255, 255, 255, 0.50) solid',
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                    '&:hover fieldset': {
                      borderColor: 'transparent',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'transparent',
                    },
                    '& input': {
                      color: '#BBBBBB',
                      fontSize: '14px',
                      fontFamily: 'Raleway',
                    },
                  },
                }}
              />

              <TextField
                name="projectDescription"
                placeholder="Project Description"
                value={formData.projectDescription}
                onChange={handleChange}
                required
                multiline
                rows={3}
                sx={{
                  width: { xs: '100%', md: '303px' },
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    outline: '1px rgba(255, 255, 255, 0.50) solid',
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                    '&:hover fieldset': {
                      borderColor: 'transparent',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'transparent',
                    },
                    '& textarea': {
                      color: '#BBBBBB',
                      fontSize: '14px',
                      fontFamily: 'Raleway',
                    },
                  },
                }}
              />

              <TextField
                name="techStack"
                placeholder="Tech Stack"
                value={formData.techStack}
                onChange={handleChange}
                sx={{
                  width: { xs: '100%', md: '303px' },
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    outline: '1px rgba(255, 255, 255, 0.50) solid',
                    '& fieldset': {
                      borderColor: 'transparent',
                    },
                    '&:hover fieldset': {
                      borderColor: 'transparent',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'transparent',
                    },
                    '& input': {
                      color: '#BBBBBB',
                      fontSize: '14px',
                      fontFamily: 'Raleway',
                    },
                  },
                }}
              />

              <FormControl component="fieldset" sx={{ mt: 1, width: '100%' }}>
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: '14px',
                    fontFamily: 'Raleway',
                    marginBottom: '4px',
                  }}
                >
                  Timeline *
                </Typography>
                <RadioGroup
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  row={!isMobile}
                  sx={{
                    '& .MuiFormControlLabel-root': {
                      marginRight: '8px',
                      marginBottom: isMobile ? '8px' : '0',
                    },
                    flexDirection: isMobile ? 'column' : 'row',
                  }}
                >
                  <FormControlLabel
                    value="1 - 3 months"
                    control={<Radio sx={{ color: 'white' }} />}
                    label={
                      <Typography sx={{ color: 'white', fontSize: '14px', fontFamily: 'Raleway' }}>
                        1 - 3 months
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="3 - 6 months"
                    control={<Radio sx={{ color: 'white' }} />}
                    label={
                      <Typography sx={{ color: 'white', fontSize: '14px', fontFamily: 'Raleway' }}>
                        3 - 6 months
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="1 year"
                    control={<Radio sx={{ color: 'white' }} />}
                    label={
                      <Typography sx={{ color: 'white', fontSize: '14px', fontFamily: 'Raleway' }}>
                        1 year
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="More than one year"
                    control={<Radio sx={{ color: 'white' }} />}
                    label={
                      <Typography sx={{ color: 'white', fontSize: '14px', fontFamily: 'Raleway' }}>
                        More than one year
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>

              <Box sx={{ width: '100%' }}>
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: '14px',
                    fontFamily: 'Raleway',
                    marginBottom: '8px',
                  }}
                >
                  Set up call
                </Typography>
                <TextField
                  name="date"
                  type="date"
                  value={formData.date}
                  onChange={handleChange}
                  sx={{
                    width: { xs: '100%', md: '303px' },
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.15)',
                      borderRadius: '6px',
                      outline: '1px rgba(255, 255, 255, 0.50) solid',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: 'transparent',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'transparent',
                      },
                      '& input': {
                        color: '#BBBBBB',
                        fontSize: '14px',
                        fontFamily: 'Raleway',
                      },
                    },
                  }}
                />
              </Box>

              <Button
                type="submit"
                variant="outlined"
                sx={{
                  width: { xs: '100%', sm: '130.68px' },
                  padding: '15px 25px',
                  borderRadius: '35px',
                  borderColor: '#6DFCFF',
                  color: '#6DFCFF',
                  fontSize: '16px',
                  fontFamily: 'Raleway',
                  fontWeight: 500,
                  backdropFilter: 'blur(5.85px)',
                  '&:hover': {
                    borderColor: '#6DFCFF',
                  },
                  mt: 1,
                }}
              >
                Send
              </Button>

              {RECAPTCHA_KEY && (
                <Box sx={{ width: '100%', mt: 2, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <ReCAPTCHA
                    sitekey={RECAPTCHA_KEY}
                    onChange={handleRecaptchaChange}
                    size={isMobile ? "compact" : "normal"}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ContactSection; 